import { PageLayout } from '../../PageLayout';
import { TextSpan } from '../../Atoms/Text';
import { Card } from '../../Atoms/Card';
import { TeamMember, defaultState, useGeneralStore } from '../../../store/GeneralStore';
import { fetchMyTeam } from '../../../Api/MyTeam/fetchMyTeam';
import { useEffect, useState } from 'react';
import { useFetching } from '../../../Context/FetchingContext';
import { objectNotEmpty } from '../../../utils/helpers';
import { ContactUsLink } from '../../Compounds/ContactUs/ContactUsLink';
import { Account } from '../../../types';
import { Session, getSessionSubject } from '../../../Api/useSession';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { addError } from '../../../store/Error';
import { AccountSelectionForm } from '../../Compounds/UserCreation/AccountSelectionForm';
import { getAccounts, getAccountScope } from '../../../store/User';
const DEFAULT_AVATAR_PATH = '/my-team-default-avatar.png';

export const MyTeamPage = () => {
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const {
    currentState: { myTeam },
    updateState,
    setErrorMessageData
  } = useGeneralStore();
  const { setFetching, isFetching } = useFetching();
  const { success_manager: successManager } = myTeam;
  const accounts = getAccounts();
  const [session, setSession] = useState<Session | undefined>(undefined);
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>(
    getAccountScope()?.length === 1 ? getAccountScope()?.[0] : undefined
  );
  // A single-account scope is set before the fetch can start, so "nobody is assigned" has to wait for an answer.
  const [teamSettled, setTeamSettled] = useState(false);

  const emitErrorState = (message?: string): void => addError('my-team-page', message);
  const answeredWithoutManager = teamSettled && !successManager.email;

  const getData = async () => {
    setFetching(true);

    const { data, error } = await fetchMyTeam(selectedAccount?.id);

    if (error) {
      setErrorMsg(error?.message);
      setErrorMessageData(error?.message);
      // Left as it was, the previous account's manager would keep standing under the new selection.
      updateState({ myTeam: defaultState.myTeam });
    }
    if (data) {
      setErrorMsg(undefined);
      updateState({ myTeam: data });
    }

    setTeamSettled(true);
    setFetching(false);
  };

  useEffect(() => {
    setTeamSettled(false);
    if (selectedAccount?.id && session?.token) getData();
  }, [selectedAccount?.id, session?.token]);

  useEffect(() => {
    const sessionSub = getSessionSubject().subscribe((session) => setSession(session));

    return () => {
      if (sessionSub) sessionSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (errorMsg) emitErrorState(errorMsg);
  }, [errorMsg]);

  const pageActions = () => {
    return (
      <AccountSelectionForm
        accounts={accounts || []}
        disabled={accounts?.length === 1}
        setSelectedAccount={(v) => setSelectedAccount(v)}
        selectedAccount={selectedAccount || null}
        styles="flex items-center w-72 pb-4 mr-auto"
        showLabel={true}
        inputStyles="h-[1.75rem]"
      />
    );
  };

  return (
    <PageLayout
      enableActions={accounts?.length !== 1}
      pageTitle="My Team"
      pageActions={pageActions()}>
      {!isFetching && !selectedAccount && (
        <div className="w-full flex justify-center items-center mt-20">
          <TextSpan
            size="md"
            className="self-center">
            Please select an account to view team data.
          </TextSpan>
        </div>
      )}
      {isFetching && (
        <div className="flex mt-40">
          <ComponentSpinner label="Loading Data" />
        </div>
      )}
      {!isFetching && selectedAccount && (
        <>
          <div className="flex mt-10">
            <TextSpan
              className="text-xl font-medium"
              dataCy="my-team-subhead"
              dataTestId="my-team-subhead">
              Need to touch base on your services?
            </TextSpan>
          </div>
          <div className="grid grid-cols-1 mt-8 gap-8 max-w-6xl">
            {successManager.email && <TeamMemberCard member={successManager} />}
            {answeredWithoutManager && errorMsg && (
              <TextSpan
                dataCy="my-team-not-loaded"
                dataTestId="my-team-not-loaded">
                Your team could not be loaded, so this page cannot show who is assigned.
              </TextSpan>
            )}
            {answeredWithoutManager && !errorMsg && (
              <TextSpan
                dataCy="my-team-no-account-manager"
                dataTestId="my-team-no-account-manager">
                No Account Manager is assigned to this account.
              </TextSpan>
            )}
          </div>
          <div>
            <hr className="mt-6 mb-3" />
            <TextSpan>Need help with the portal, or want to provide us feedback on the portal?</TextSpan>
            <ContactUsLink
              dataTestId="my-team-support"
              linkText="Contact your support staff"
              textStyle="text-sm"
            />
          </div>
        </>
      )}
    </PageLayout>
  );
};

interface TeamMemberCardProps {
  member?: TeamMember;
}

const TeamMemberCard = ({ member }: TeamMemberCardProps) => {
  if (!member || !objectNotEmpty(member)) return null;

  const { firstName, lastName, email, phone, title, imgUrl } = member;
  const name = `${firstName || ''} ${lastName}`;
  const testTitle = title?.replace(/\s+/g, '-');
  return (
    <Card className="flex max-w-xl">
      <img
        alt={`${title} ${name}`}
        className="ml-[3.5%] self-center h-4/5"
        onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR_PATH)}
        src={imgUrl}
      />
      <div className="m-5 ml-[7%]">
        <TextSpan
          className="text-xl font-medium w-4/5 block mb-5"
          dataCy={testTitle}
          dataTestId={title}>
          {`Your ${title}:`}
        </TextSpan>
        <TextSpan
          className="text-md font-medium block"
          dataCy={`${testTitle}-name`}
          dataTestId={`${title} name`}>
          {name}
        </TextSpan>
        <TextSpan
          className="text-md font-medium block break-all"
          dataCy={`${testTitle}-email`}
          dataTestId={`${title} email`}>
          {email}
        </TextSpan>
        {!!phone && (
          <TextSpan
            className="text-md font-medium block"
            dataCy={`${testTitle}-phone`}
            dataTestId={`${title} phone`}>
            {phone}
          </TextSpan>
        )}
      </div>
    </Card>
  );
};
